import { ReactNode, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ChevronDown, LucideIcon } from 'lucide-react'
import { DUR } from '../motion/motionTokens'

interface CollapsibleSectionProps {
  title: string
  icon?: LucideIcon
  meta?: ReactNode
  defaultOpen?: boolean
  children: ReactNode
}

// A settings/section block whose body collapses behind a clickable header. The
// header reads as a broadcast active-tab bar: a filled bar with a left accent
// that turns gold when the section is open.
export function CollapsibleSection({
  title,
  icon: Icon,
  meta,
  defaultOpen = false,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen)
  const reduced = useReducedMotion()

  return (
    <section className="collapsible" data-open={open ? 'true' : 'false'}>
      <button
        type="button"
        className="collapsible-header"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
      >
        <span className="collapsible-title">{Icon && <Icon size={14} />} {title}</span>
        {meta && <span className="collapsible-meta">{meta}</span>}
        <span className="collapsible-toggle">
          <ChevronDown size={16} className="collapsible-chevron" />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            className="collapsible-body"
            initial={reduced ? { opacity: 0 } : { height: 0, opacity: 0 }}
            animate={reduced ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
            exit={reduced ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={{ duration: DUR.base, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="collapsible-body-inner">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
